import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Counter, Slider, Toggle } from '../components/Card';
import { MealBlock, ShareMealsButton } from '../components/MealBlock';
import { TomorrowPrep } from '../components/TomorrowPrep';
import { Chip } from '../components/Chip';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { MacroStrip } from '../components/MacroStrip';
import { Nav } from '../components/Nav';
import { LoadingState } from '../components/LoadingState';
import { PushCard } from '../components/PushCard';
import { PhotosCard } from '../components/PhotosCard';
import { SupplementsCard } from '../components/SupplementsCard';
import { usePatchDay } from '../lib/usePatchDay';
import { useAuth } from '../lib/auth';
import { filteredIngredients } from '../lib/meals';
import { formatMealPlanText } from '../lib/mealText';
import {
  api,
  type Bet,
  type DailyLog,
  type DashboardPayload,
  type FoodEstimate,
  type Meal,
  type MealEntry,
  type TodayTraining,
} from '../lib/api';
import type { ChipTone } from '../components/Chip';

export default function Today() {
  const { state: authState, signOut } = useAuth();
  const [bet, setBet] = useState<Bet | null>(null);
  const [log, setLog] = useState<DailyLog | null>(null);
  const [training, setTraining] = useState<TodayTraining | null>(null);
  const [adherence, setAdherence] = useState<DashboardPayload['adherence'] | null>(null);
  const [eatingWindow, setEatingWindow] = useState<DashboardPayload['eating_window'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  // null = the field mirrors log.steps. A string = the user is typing, and an
  // empty string has to survive on screen long enough to retype into.
  const [stepsInput, setStepsInput] = useState<string | null>(null);
  const [offPlanDesc, setOffPlanDesc] = useState('');
  const [offPlanKcal, setOffPlanKcal] = useState('');
  // After ✨ Estimate, the AI's split items live here as a preview queue.
  // Empty array = no preview, manual-entry path is active. Non-empty = the
  // queue is shown and "Log all" commits each as a separate off-plan entry.
  const [offPlanItems, setOffPlanItems] = useState<FoodEstimate[]>([]);
  const [offPlanEstimating, setOffPlanEstimating] = useState(false);
  const [offPlanEstimateError, setOffPlanEstimateError] = useState<string | null>(null);
  const offPlanEstimateRef = useRef(false);

  const date = log?.log_date ?? '';
  // commit merges a day returned by one of the non-PATCH endpoints below.
  // Those responses are awaited, so a counter tapped while the request was open
  // is newer than the number the server answered with — commit re-applies every
  // such edit on top instead of letting the response roll it back.
  const { patch, commit, status: saveStatus, error: saveError } = usePatchDay(date, log, setLog);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const today = await api.getToday();
      setLog(today);
      if (today.weight_kg !== null) setWeightInput(String(today.weight_kg));
      try {
        setBet(await api.getBet());
      } catch {
        setBet(null);
      }
      try {
        setTraining(await api.getTodayTraining());
      } catch {
        setTraining(null);
      }
      try {
        const dash = await api.getDashboard();
        setAdherence(dash.adherence);
        setEatingWindow(dash.eating_window ?? null);
      } catch {
        setAdherence(null);
        setEatingWindow(null);
      }
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const commitBet = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.initBet();
      await refresh();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveWeight = async () => {
    const n = Number(weightInput);
    if (!Number.isFinite(n) || n < 30 || n > 250) {
      setError('weight must be a number between 30 and 250');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.setWeight(n);
      commit(next);
      // Server may have auto-recalibrated the bet on this weigh-in; refresh
      // so the new starting weight + recalibrated_at flag surface immediately.
      try {
        setBet(await api.getBet());
      } catch {
        // ignore — the weight save itself succeeded
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  // Manual-entry path: user typed both description and kcal, no AI involved.
  // POSTs a single off-plan entry. Used only when the preview queue is empty.
  const addOffPlanManual = async () => {
    if (!log) return;
    const desc = offPlanDesc.trim();
    const kcal = Number(offPlanKcal);
    if (desc.length === 0) {
      setError('describe what you ate');
      return;
    }
    if (!Number.isFinite(kcal) || kcal < 0 || kcal > 5000) {
      setError('estimate kcal between 0 and 5000');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { day } = await api.addOffPlanFood(log.log_date, desc, Math.round(kcal));
      commit(day);
      setOffPlanDesc('');
      setOffPlanKcal('');
      setOffPlanEstimateError(null);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  // estimateOffPlan asks the coach to split the description into items and
  // populates the preview queue. Each item has its own description + macros.
  // Nothing is logged yet — the user must press "Log all" or "Discard".
  const estimateOffPlan = async () => {
    if (offPlanEstimateRef.current) return;
    const desc = offPlanDesc.trim();
    if (desc.length === 0) return;
    offPlanEstimateRef.current = true;
    setOffPlanEstimating(true);
    setOffPlanEstimateError(null);
    try {
      const r = await api.estimateFood(desc);
      if (r.items.length === 0) {
        setOffPlanEstimateError('Couldn’t estimate — try a more specific description.');
      } else {
        setOffPlanItems(r.items);
      }
    } catch (e) {
      const msg = String(e);
      if (msg.includes('503') || msg.includes('coach_disabled')) {
        setOffPlanEstimateError('AI estimate unavailable — enter kcal manually.');
      } else if (msg.includes('502') || msg.includes('declined') || msg.includes('vague')) {
        setOffPlanEstimateError('Couldn’t estimate — try a more specific description.');
      } else {
        setOffPlanEstimateError(msg);
      }
    } finally {
      offPlanEstimateRef.current = false;
      setOffPlanEstimating(false);
    }
  };

  // Log all preview items as separate off-plan entries. POSTs in parallel
  // and uses allSettled so one failure (e.g. hitting the 10-cap-per-day
  // ceiling) doesn't abandon the rest. On any failure, surviving failed
  // items stay in the preview so the user can retry or remove them.
  const logAllOffPlanItems = async () => {
    if (!log || offPlanItems.length === 0) return;
    setBusy(true);
    setError(null);
    const date = log.log_date;
    const results = await Promise.allSettled(
      offPlanItems.map((it) =>
        api.addOffPlanFood(date, it.description, it.kcal, {
          protein_g: it.protein_g,
          fat_g: it.fat_g,
          carbs_g: it.carbs_g,
        }),
      ),
    );
    const failed: { item: FoodEstimate; reason: string }[] = [];
    let lastDay: DailyLog | null = null;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        lastDay = r.value.day;
      } else {
        failed.push({ item: offPlanItems[i], reason: String(r.reason) });
      }
    });
    if (lastDay) commit(lastDay);
    setBusy(false);
    if (failed.length === 0) {
      setOffPlanItems([]);
      setOffPlanDesc('');
      setOffPlanKcal('');
      setOffPlanEstimateError(null);
    } else {
      setOffPlanItems(failed.map((f) => f.item));
      setError(`${failed.length} item(s) failed to log: ${failed.map((f) => f.reason).join('; ')}`);
    }
  };

  const removeOffPlanItem = (idx: number) => {
    setOffPlanItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const discardOffPlanPreview = () => {
    setOffPlanItems([]);
    setOffPlanEstimateError(null);
  };

  // Editing the description invalidates any prior estimate — the items
  // would be tied to the old text. Clearing forces the user to re-estimate.
  const onOffPlanDescChange = (v: string) => {
    setOffPlanDesc(v);
    if (offPlanItems.length > 0) {
      setOffPlanItems([]);
      setOffPlanEstimateError(null);
    }
  };

  const removeOffPlan = async (id: number) => {
    if (!log) return;
    setBusy(true);
    setError(null);
    try {
      const day = await api.deleteOffPlanFood(log.log_date, id);
      commit(day);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const recalibrateFromCurrent = async () => {
    const w = log?.weight_kg;
    if (w === undefined || w === null) {
      setError('log a weight first');
      return;
    }
    const delta = (w - (bet?.starting_weight_kg ?? 95)).toFixed(1);
    if (!confirm(`Reset bet starting weight to ${w} kg and shift every weekly target by ${delta} kg?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.recalibrateBet(w);
      setBet(next);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleMeal = async (mealId: MealEntry['meal_id'], completed: boolean) => {
    try {
      const next = await api.setMeal(mealId, completed);
      commit(next);
    } catch (e) {
      setError(String(e));
    }
  };

  if (!log) {
    return (
      <>
        <div className="min-h-full flex items-center justify-center text-fg-faint pb-24">
          {error ? (
            <pre className="text-danger text-sm">{error}</pre>
          ) : (
            <LoadingState />
          )}
        </div>
        <Nav />
      </>
    );
  }

  const userEmail = authState.status === 'authed' ? authState.user.email : '';
  const isMonday = new Date(log.log_date + 'T12:00:00').getDay() === 1;
  const shownMeals = visibleMeals(log.meals, training);
  // The share text is built from the very templates the cards below render,
  // so what gets sent is what is on screen. No template loaded = nothing to
  // share, and the action is left off entirely.
  const shownTemplates = shownMeals
    .map((m) => training?.meals?.meals?.find((tm) => tm.id === m.meal_id))
    .filter((tm): tm is Meal => Boolean(tm));
  const shareTodayText = () =>
    formatMealPlanText({
      dateISO: log.log_date,
      block: training?.block,
      weekNumber: training?.week_number,
      meals: shownTemplates,
      dailyTotals: training?.meals?.daily_totals,
      eatingWindow,
    });

  return (
    <>
      <div className="min-h-full max-w-md lg:max-w-5xl mx-auto px-5 lg:px-8 pt-8 lg:pt-12 space-y-4 lg:space-y-5 pb-32 stack-2col">
        <header className="space-y-2">
          <div className="flex items-baseline justify-between">
            <p className="label-eyebrow">Gios Protocol</p>
            <SaveBadge status={saveStatus} />
          </div>
          <h1 className="font-display text-5xl leading-none">Today</h1>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-fg-muted">{log.log_date}</p>
          {bet ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Chip tone="muted">{bet.days_to_deadline} days left</Chip>
              <Chip tone="muted">target {bet.target_weight_kg} kg</Chip>
              {training && training.week_number > 0 && (
                <>
                  <Chip tone="neutral">Week {training.week_number}</Chip>
                  <Chip tone="muted">Block {training.block}</Chip>
                </>
              )}
            </div>
          ) : (
            <Chip tone="muted" dot>Bet not committed</Chip>
          )}
        </header>

        {adherence && adherence.tier !== 'no_data' && (
          <AdherenceBanner adherence={adherence} />
        )}

        <MacroStrip macros={log.macros} />

        {!bet && (
          <Card eyebrow="Commit to the bet">
            <p className="text-sm text-fg-muted leading-relaxed">
              95.0 kg → 72.0 kg by 31 Dec 2026. Buffer 70.0 kg by 15 Dec. $150,000 on the line. Tapping
              below stamps the start. There is no undo.
            </p>
            <button onClick={commitBet} disabled={busy} className="btn-primary w-full py-3.5 mt-2">
              I commit
            </button>
          </Card>
        )}

        <Card eyebrow="Weight" hint="Pre-food, pre-coffee, post-bathroom.">
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min={30}
              max={250}
              value={weightInput}
              onChange={(e) => setWeightInput(e.target.value)}
              className="flex-1 rounded-control bg-bg-sunken border border-line px-3 py-2.5 font-mono text-lg tabular-nums text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
              placeholder="kg"
            />
            <button onClick={saveWeight} disabled={busy || !weightInput} className="btn-primary px-5">
              Save
            </button>
          </div>
          {log.weight_kg !== null && (
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-fg-faint">
              Logged <span className="text-fg normal-case tracking-normal">{log.weight_kg} kg</span>
            </p>
          )}
          {bet?.recalibrated_at && (
            <Chip tone="good" dot>
              Re-anchored at {bet.starting_weight_kg.toFixed(1)} kg
            </Chip>
          )}
          {bet && !bet.recalibrated_at && log.weight_kg !== null && Math.abs(log.weight_kg - bet.starting_weight_kg) >= 0.5 && (
            <div className="rounded-panel bg-bg-sunken p-3 space-y-2">
              <p className="text-xs text-fg-muted leading-relaxed">
                Bet starts at <span className="text-fg font-medium">{bet.starting_weight_kg.toFixed(1)} kg</span>.
                Today's weight is {(log.weight_kg - bet.starting_weight_kg > 0 ? '+' : '')}
                {(log.weight_kg - bet.starting_weight_kg).toFixed(1)} kg from that. Shift every weekly target by the same amount?
              </p>
              <button
                onClick={recalibrateFromCurrent}
                disabled={busy}
                className="btn-secondary w-full py-2.5 text-xs font-mono uppercase tracking-[0.16em]"
              >
                Lock in {log.weight_kg.toFixed(1)} kg as new start
              </button>
            </div>
          )}
          <Counter
            label="Waist"
            unit="cm"
            step={0.5}
            min={50}
            max={150}
            value={log.waist_cm}
            onChange={(n) => patch({ waist_cm: n })}
          />
        </Card>

        <Card
          eyebrow="Meals"
          action={
            shownTemplates.length > 0 ? <ShareMealsButton text={shareTodayText} /> : undefined
          }
        >
          <div className="space-y-3">
            {shownMeals.map((m) => (
              <MealBlock
                key={m.meal_id}
                meal={m}
                template={training?.meals?.meals?.find((tm) => tm.id === m.meal_id)}
                onToggle={(checked) => toggleMeal(m.meal_id, checked)}
              />
            ))}
          </div>
        </Card>

        <TomorrowPrep
          todayISO={log.log_date}
          todayBlock={training?.block}
          eatingWindow={eatingWindow}
        />

        {training && (
          <Card
            eyebrow="Training"
            hint={
              (training.training_week_number ?? training.week_number) > 0
                ? `Week ${training.training_week_number ?? training.week_number} · ${capitalize(weekday())}`
                : 'Pre-week — rest day'
            }
          >
            <p className="text-sm text-fg leading-relaxed">{training.session.title}</p>
            {training.session.exercises && training.session.exercises.length > 0 && (
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                {training.session.exercises.length} exercises · est. {estimateMinutes(training.session.exercises.length)} min
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <Link to="/training" className="btn-secondary flex-1 text-center py-2.5">
                Open session
              </Link>
              <button
                onClick={() =>
                  patch(
                    { training_completed: !log.training_completed, training_session_id: training.session.id },
                    { immediate: true },
                  )
                }
                className={
                  // Done drops out of the black-pill language into the green
                  // pastel: the action is spent, so it stops competing with
                  // whatever is still unlogged on the page.
                  log.training_completed
                    ? 'btn-secondary flex-1 py-2.5 bg-good-soft! border-good/25! text-good!'
                    : 'btn-primary flex-1 py-2.5'
                }
              >
                {log.training_completed ? '✓ Logged' : 'Mark complete'}
              </button>
            </div>
          </Card>
        )}

        <Card eyebrow="Steps" hint={`Target: ${stepTarget(training)} for today.`}>
          <Counter
            label="Steps"
            step={500}
            min={0}
            max={50000}
            value={log.steps}
            onChange={(n) => patch({ steps: n })}
          />
          <input
            type="number"
            inputMode="numeric"
            min={0}
            max={50000}
            value={stepsInput ?? log.steps}
            // Clearing the field used to send `Number('')` — a silent write of
            // 0 steps from a box the user was only emptying to retype. An
            // empty (or unparseable) field is no change at all; it is held in
            // local state so it stays empty until something is typed, and
            // released on blur so the server's number takes over again.
            onChange={(e) => {
              const raw = e.target.value;
              setStepsInput(raw);
              const next = stepsPatchFrom(raw);
              if (next !== null) patch({ steps: next });
            }}
            onBlur={() => setStepsInput(null)}
            className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 font-mono tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
          />
        </Card>

        <Card eyebrow="Hydration & salt">
          <Counter label="Water" unit="L" step={0.25} max={10} value={log.water_liters ?? 0} onChange={(n) => patch({ water_liters: n })} />
          <Counter label="Salt" unit="g" step={0.5} max={20} value={log.salt_grams ?? 0} onChange={(n) => patch({ salt_grams: n })} />
        </Card>

        <SupplementsCard />

        <Card eyebrow="Mobility">
          <Toggle
            label="Mobility 10 min flow"
            checked={log.mobility_completed}
            onChange={(c) => patch({ mobility_completed: c }, { immediate: true })}
          />
        </Card>

        <CollapsibleSection
          eyebrow="Daily log"
          summary={dailyLogSummary(log)}
        >
          <div className="space-y-3">
            <Slider label="Sleep last night (h)" min={0} max={12} step={0.25} value={log.sleep_hours} onChange={(n) => patch({ sleep_hours: n })} />
            <Slider label="Sleep quality" min={1} max={10} value={log.sleep_quality_1to10} onChange={(n) => patch({ sleep_quality_1to10: n })} />
            <Slider label="Energy" min={1} max={10} value={log.energy_1to10} onChange={(n) => patch({ energy_1to10: n })} />
            <Slider label="Hunger" min={1} max={10} value={log.hunger_1to10} onChange={(n) => patch({ hunger_1to10: n })} />
          </div>
          <Toggle
            label="Alcohol consumed today"
            checked={log.alcohol_consumed}
            onChange={(c) => patch({ alcohol_consumed: c }, { immediate: true })}
            hint={log.alcohol_consumed ? 'Logged. Expect 48-72h of stalled fat loss.' : 'Zero is the rule.'}
          />
        </CollapsibleSection>

        <Card
          eyebrow="Off-plan food"
          hint="Anything outside the 2-meal plan. Estimate kcal — close-enough beats unlogged."
          action={
            (log.macros.kcal.target > 0 || (log.off_plan_kcal_total ?? 0) > 0) && (
              <div className="flex flex-wrap items-center gap-1.5 justify-end">
                {log.macros.kcal.target > 0 && (
                  <Chip
                    tone={log.macros.kcal.target - log.macros.kcal.consumed >= 0 ? 'good' : 'danger'}
                    dot
                  >
                    {log.macros.kcal.target - log.macros.kcal.consumed >= 0
                      ? `${(log.macros.kcal.target - log.macros.kcal.consumed).toLocaleString()} kcal headroom`
                      : `${(log.macros.kcal.consumed - log.macros.kcal.target).toLocaleString()} kcal over`}
                  </Chip>
                )}
                {(log.off_plan_kcal_total ?? 0) > 0 && (
                  <Chip tone="warn" dot>
                    +{log.off_plan_kcal_total} kcal today
                  </Chip>
                )}
              </div>
            )
          }
        >
          <div className="space-y-2">
            <input
              type="text"
              maxLength={200}
              value={offPlanDesc}
              onChange={(e) => onOffPlanDescChange(e.target.value)}
              placeholder="2 cheeseburgers · 1 big mac and 1 khachapuri · miso soup"
              disabled={offPlanItems.length > 0}
              className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised disabled:opacity-60"
            />

            {offPlanItems.length === 0 ? (
              <>
                <button
                  type="button"
                  onClick={estimateOffPlan}
                  disabled={offPlanEstimating || busy || !offPlanDesc.trim()}
                  className="btn-secondary w-full py-2 text-xs font-mono uppercase tracking-[0.14em] disabled:opacity-50"
                >
                  {offPlanEstimating ? 'Asking the coach…' : '✨ Estimate kcal + macros'}
                </button>
                <div className="flex gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={5000}
                    value={offPlanKcal}
                    onChange={(e) => setOffPlanKcal(e.target.value)}
                    placeholder="kcal"
                    className="w-24 rounded-control bg-bg-sunken border border-line px-3 py-2.5 font-mono tabular-nums text-sm text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
                  />
                  <button
                    onClick={addOffPlanManual}
                    disabled={busy || !offPlanDesc.trim() || !offPlanKcal}
                    className="btn-primary flex-1 px-4 py-2.5 text-sm"
                  >
                    Log
                  </button>
                </div>
              </>
            ) : (
              <OffPlanPreview
                items={offPlanItems}
                busy={busy}
                onRemove={removeOffPlanItem}
                onLogAll={logAllOffPlanItems}
                onDiscard={discardOffPlanPreview}
              />
            )}

            {offPlanEstimateError && (
              <p role="alert" className="text-xs text-warn">
                {offPlanEstimateError}
              </p>
            )}
          </div>
          {(log.off_plan_food ?? []).length > 0 && (
            <ul className="space-y-1.5">
              {(log.off_plan_food ?? []).map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-col gap-0.5 rounded-panel bg-bg-sunken px-3 py-2 text-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex-1 truncate text-fg">{entry.description}</span>
                    <span className="font-mono tabular-nums text-fg-muted shrink-0">
                      +{entry.est_kcal}
                    </span>
                    <button
                      onClick={() => removeOffPlan(entry.id)}
                      disabled={busy}
                      className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-danger transition-colors"
                    >
                      remove
                    </button>
                  </div>
                  {entry.protein_g !== null && entry.fat_g !== null && entry.carbs_g !== null && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                      {entry.protein_g}p · {entry.fat_g}f · {entry.carbs_g}c
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <CollapsibleSection
          eyebrow="Notes"
          summary={log.notes && log.notes.length > 0 ? log.notes.slice(0, 60) : 'No notes'}
        >
          <textarea
            value={log.notes ?? ''}
            onChange={(e) => patch({ notes: e.target.value })}
            rows={3}
            placeholder="Anything off-protocol or worth remembering."
            className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint resize-none transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
          />
        </CollapsibleSection>

        <CollapsibleSection eyebrow="Photos & push" summary="Tap to expand">
          <PhotosCard date={log.log_date} isMonday={isMonday} />
          <PushCard />
        </CollapsibleSection>

        {(error || saveError) && (
          <pre className="text-xs text-danger whitespace-pre-wrap break-all">{error || saveError}</pre>
        )}

        <footer className="pt-4 border-t border-line flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
          <span className="normal-case tracking-normal text-fg-muted">{userEmail}</span>
          <button onClick={() => void signOut()} className="hover:text-fg transition-colors">
            Sign out
          </button>
        </footer>
      </div>
      <Nav />
    </>
  );
}

// stepsPatchFrom turns what is in the steps field into the value to save, or
// null for "don't save anything". An empty box is someone about to retype, not
// a day with zero steps — `Number('')` is 0 and used to write that straight to
// the server. Exported for unit tests.
export function stepsPatchFrom(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

function OffPlanPreview({
  items,
  busy,
  onRemove,
  onLogAll,
  onDiscard,
}: {
  items: FoodEstimate[];
  busy: boolean;
  onRemove: (idx: number) => void;
  onLogAll: () => void;
  onDiscard: () => void;
}) {
  const totals = items.reduce(
    (acc, it) => ({
      kcal: acc.kcal + it.kcal,
      p: acc.p + it.protein_g,
      f: acc.f + it.fat_g,
      c: acc.c + it.carbs_g,
    }),
    { kcal: 0, p: 0, f: 0, c: 0 },
  );
  // Nothing here is logged yet, so the panel wears the "planned" violet on a
  // plain grey well rather than a pastel fill — the meta text inside it needs a
  // surface it actually passes contrast against.
  return (
    <div className="rounded-panel bg-bg-sunken p-2.5 space-y-2">
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-pop">
        Will log {items.length} item{items.length === 1 ? '' : 's'}
      </p>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li
            key={`${i}-${it.description}`}
            className="flex items-center gap-2 text-sm"
          >
            <span className="flex-1 min-w-0 truncate text-fg">{it.description}</span>
            <span className="font-mono tabular-nums text-fg-muted shrink-0">
              {it.kcal} kcal
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint shrink-0">
              {it.protein_g}p · {it.fat_g}f · {it.carbs_g}c
            </span>
            <button
              type="button"
              onClick={() => onRemove(i)}
              disabled={busy}
              aria-label="Remove item"
              className="text-fg-faint hover:text-danger transition-colors text-sm leading-none"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-baseline justify-between gap-2 pt-1.5 border-t border-line">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
          Total
        </span>
        <span className="font-mono tabular-nums text-sm text-fg">
          {totals.kcal} kcal · {totals.p}p · {totals.f}f · {totals.c}c
        </span>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onLogAll}
          disabled={busy || items.length === 0}
          className="btn-primary flex-1 py-2.5 text-sm"
        >
          Log {items.length} item{items.length === 1 ? '' : 's'}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          className="btn-secondary flex-1 py-2.5 text-sm"
        >
          Discard
        </button>
      </div>
    </div>
  );
}

function SaveBadge({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'idle') return null;
  if (status === 'saving') return <Chip tone="muted">Saving…</Chip>;
  if (status === 'saved') return <Chip tone="good" dot>Saved</Chip>;
  return <Chip tone="danger" dot>Failed</Chip>;
}

function AdherenceBanner({ adherence }: { adherence: DashboardPayload['adherence'] }) {
  const tone = adherenceTone(adherence.tier);
  return (
    <div className="surface-raised p-5 space-y-2">
      <Chip tone={tone} dot>
        {adherence.tier}
      </Chip>
      <p className="text-sm text-fg leading-relaxed">{adherence.message}</p>
    </div>
  );
}

// dailyLogSummary builds the one-line preview shown next to the eyebrow
// when "Daily log" is collapsed. We stitch sleep/energy/alcohol so the user
// can glance and skip expanding when they've already logged everything.
function dailyLogSummary(log: DailyLog): string {
  const parts: string[] = [];
  if (log.sleep_hours !== null && log.sleep_hours !== undefined) {
    parts.push(`Sleep ${log.sleep_hours}h`);
  }
  if (log.energy_1to10 !== null && log.energy_1to10 !== undefined) {
    parts.push(`Energy ${log.energy_1to10}`);
  }
  if (log.alcohol_consumed) {
    parts.push('Alcohol');
  }
  if (parts.length === 0) return 'Tap to log';
  return parts.join(' · ');
}

function adherenceTone(tier: string): ChipTone {
  switch (tier) {
    case 'crushing':
      return 'good';
    case 'nudge':
      return 'accent';
    case 'warning':
    case 'locked':
      return 'warn';
    case 'coach':
      return 'danger';
    default:
      return 'muted';
  }
}

// visibleMeals filters daily-log meal rows down to only those the active
// template defines. ensureDailyLog inserts a row for every legacy slot
// (breakfast/lunch/dinner) so old code keeps compiling, but Block A's 2-meal
// carnivore plan only declares two — without this filter, a phantom "Dinner"
// toggle leaks into the UI. If no template is loaded yet, render everything
// (avoids hiding meals on the first paint).
function visibleMeals(meals: MealEntry[], training: TodayTraining | null): MealEntry[] {
  const tplIDs = training?.meals?.meals?.map((m) => m.id);
  if (!tplIDs || tplIDs.length === 0) return meals;
  return meals.filter((m) => tplIDs.includes(m.meal_id));
}

// mealIngredients renders the ingredient list as a single joined string.
// Kept for tests and any caller that wants the compact one-line form.
// MealBlock uses filteredIngredients() directly to render a vertical list.
export { SEASONING_AMOUNTS, SEASONING_NAME_RE } from '../lib/meals';
export function mealIngredients(m: MealEntry, training: TodayTraining | null): string | undefined {
  const tplMeal = training?.meals?.meals?.find((tm) => tm.id === m.meal_id);
  if (!tplMeal) return undefined;
  const lines = filteredIngredients(tplMeal).map((i) => `${i.amount} ${i.item}`.trim());
  return lines.length > 0 ? lines.join(' · ') : undefined;
}

function weekday(): string {
  return new Date().toLocaleDateString([], { weekday: 'long' });
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function estimateMinutes(numExercises: number): number {
  return Math.max(20, numExercises * 5);
}

function stepTarget(training: TodayTraining | null): string {
  if (!training) return '14,000';
  return training.meals?.step_target ? training.meals.step_target.toLocaleString() : '14,000';
}
